/**
 * [라이브 미디어 세션 레지스트리] — "회의 중 갑자기 튕김" 방어의 공유축(0-1).
 *
 * definitive-refresh 실패나 401/403 시의 하드 리다이렉트는 회의 페이지를 언로드시켜
 * 참가자를 튕겨낸다. 경로 판정(`isLiveMeetingPath`)만으로는 embed/iframe 프리뷰나
 * `/prejoin` 접속 직후처럼 "경로는 회의가 아니지만 라이브 Room이 살아있는" 순간을 못 잡는다.
 *
 * 이 레지스트리는 살아있는 LiveKit Room을 모듈 스코프 Set에 등록해, "지금 보호할 라이브
 * 미디어 세션이 있는가?"를 **Room 상태에서 파생**하여 판정한다. 정확성은 상태 파생으로
 * 불변(스테일 구조적 소멸)이며, `hasLiveMediaSession()` 호출 시 Disconnected 엔트리를
 * 지연 제거해 release() 누락 경로가 있어도 메모리 누출을 자기청소한다.
 *
 * 프레임별 모듈 realm 분리로, embed iframe의 Room은 부모 대시보드의 이 Set에 등록되지 않는다.
 */
import { ConnectionState } from 'livekit-client';
import { isLiveMeetingPath } from './liveMeetingPath';

/** 등록에 필요한 최소 계약 — 읽기전용 연결 상태만 관찰한다(Room 전체 의존 회피, 테스트 용이). */
interface LiveMediaRoomLike {
  readonly state: ConnectionState;
}

/** 살아있는(등록된) Room 집합. 모듈 스코프 = 프레임(realm)별 단일 인스턴스. */
const liveRooms = new Set<LiveMediaRoomLike>();

/**
 * Room을 라이브 미디어 세션으로 등록하고, 등록 해제 함수를 반환한다.
 * 훅 마운트 시 호출하고, 언마운트 cleanup 에서 반환된 함수를 호출한다.
 */
export function acquireLiveMediaSession(room: LiveMediaRoomLike): () => void {
  liveRooms.add(room);
  return () => {
    liveRooms.delete(room);
  };
}

/**
 * 보호할 라이브 미디어 세션이 하나라도 있는지 반환한다.
 *
 * 호출 시 Disconnected 엔트리를 먼저 지연 제거한다:
 *  - 정확성은 이미 상태 파생으로 불변(끊긴 Room은 세지 않음).
 *  - 지연 제거는 메모리 위생(release() 누락 경로가 있어도 완전 누출 방지).
 */
export function hasLiveMediaSession(): boolean {
  for (const room of liveRooms) {
    if (room.state === ConnectionState.Disconnected) {
      liveRooms.delete(room);
    }
  }
  return liveRooms.size > 0;
}

/**
 * 자격증명(토큰/쿠키)을 보존해야 하는 컨텍스트인지 판정한다.
 *
 * 라이브 회의 경로(`isLiveMeetingPath`)이거나, 라이브 미디어 세션이 살아있으면 true.
 * 경로 목록은 재나열하지 않고 기존 단일 소스(`lib/liveMeetingPath.ts`)를 재사용한다.
 */
export function isCredentialPreservingContext(pathname: string | null | undefined): boolean {
  return hasLiveMediaSession() || isLiveMeetingPath(pathname);
}
